import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Building2, Plus, Mail, Trash2, Edit2, CreditCard, Check, X, Clock, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminOrganizationsPage = () => {
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);
  const [tours, setTours] = useState([]);
  const [formData, setFormData] = useState({
    name: '', contact_email: '', report_emails: [''], tour_ids: [], plan: 'monthly', report_frequency: 'monthly', notes: ''
  });
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (token && user?.account_type === 'admin') {
      fetchOrganizations();
      fetchTours();
      
      // Check for returning from Stripe
      const sessionId = searchParams.get('session_id');
      const orgId = searchParams.get('org_id');
      if (sessionId && orgId) {
        pollPaymentStatus(sessionId, orgId);
      }
    }
  }, [token, user]);

  const fetchOrganizations = async () => {
    try {
      const res = await axios.get(`${API}/admin/organizations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrganizations(res.data);
    } catch (err) {
      toast.error('Failed to load organizations');
    } finally {
      setLoading(false);
    }
  };

  const fetchTours = async () => {
    try {
      const res = await axios.get(`${API}/admin/tours`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTours(res.data);
    } catch (err) {}
  };

  const pollPaymentStatus = async (sessionId, orgId, attempts = 0) => {
    if (attempts >= 5) {
      toast.error('Payment status check timed out.');
      return;
    }
    try {
      const res = await axios.get(`${API}/admin/organizations/payment-status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.payment_status === 'paid') {
        toast.success('Payment successful! Subscription activated.');
        fetchOrganizations();
        // Clean URL
        window.history.replaceState({}, '', '/admin/organizations');
        return;
      }
      if (res.data.status === 'expired') {
        toast.error('Payment session expired.');
        return;
      }
      setTimeout(() => pollPaymentStatus(sessionId, orgId, attempts + 1), 2000);
    } catch (err) {
      toast.error('Error checking payment status');
    }
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.contact_email) {
      toast.error('Name and contact email are required');
      return;
    }
    const validEmails = formData.report_emails.filter(e => e.trim());
    if (validEmails.length === 0) {
      toast.error('At least one report email is required');
      return;
    }
    if (formData.tour_ids.length === 0) {
      toast.error('Select at least one tour');
      return;
    }

    try {
      const payload = { ...formData, report_emails: validEmails };
      if (editingOrg) {
        await axios.put(`${API}/admin/organizations/${editingOrg.org_id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Organization updated');
      } else {
        await axios.post(`${API}/admin/organizations`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Organization created');
      }
      setShowForm(false);
      setEditingOrg(null);
      setFormData({ name: '', contact_email: '', report_emails: [''], tour_ids: [], plan: 'monthly', report_frequency: 'monthly', notes: '' });
      fetchOrganizations();
    } catch (err) {
      toast.error('Failed to save organization');
    }
  };

  const handleDelete = async (orgId) => {
    if (!window.confirm('Delete this organization?')) return;
    try {
      await axios.delete(`${API}/admin/organizations/${orgId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Organization deleted');
      fetchOrganizations();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleStartSubscription = async (orgId) => {
    try {
      const res = await axios.post(`${API}/admin/organizations/${orgId}/create-subscription`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error('Failed to create subscription');
    }
  };

  const handleEdit = (org) => {
    setEditingOrg(org);
    setFormData({
      name: org.name,
      contact_email: org.contact_email,
      report_emails: org.report_emails.length > 0 ? org.report_emails : [''],
      tour_ids: org.tour_ids,
      plan: org.plan,
      report_frequency: org.report_frequency || 'monthly',
      notes: org.notes || ''
    });
    setShowForm(true);
  };

  const addEmailField = () => {
    setFormData(prev => ({ ...prev, report_emails: [...prev.report_emails, ''] }));
  };

  const updateEmail = (index, value) => {
    setFormData(prev => {
      const emails = [...prev.report_emails];
      emails[index] = value;
      return { ...prev, report_emails: emails };
    });
  };

  const removeEmail = (index) => {
    setFormData(prev => ({
      ...prev, report_emails: prev.report_emails.filter((_, i) => i !== index)
    }));
  };

  const toggleTour = (tourId) => {
    setFormData(prev => ({
      ...prev,
      tour_ids: prev.tour_ids.includes(tourId)
        ? prev.tour_ids.filter(id => id !== tourId)
        : [...prev.tour_ids, tourId]
    }));
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-teal-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <button onClick={() => navigate('/admin')} className="flex items-center text-teal-400 hover:text-teal-300 mb-4">
            <ArrowLeft className="w-5 h-5 mr-1" /> Admin
          </button>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Building2 className="w-10 h-10 text-teal-400" />
              <div>
                <h1 className="text-3xl font-bold" data-testid="organizations-title">Organizations</h1>
                <p className="text-slate-400">Manage analytics subscriptions</p>
              </div>
            </div>
            <button
              onClick={() => { setShowForm(true); setEditingOrg(null); setFormData({ name: '', contact_email: '', report_emails: [''], tour_ids: [], plan: 'monthly', report_frequency: 'monthly', notes: '' }); }}
              data-testid="add-org-button"
              className="flex items-center gap-2 bg-teal-500 hover:bg-teal-600 text-white px-4 py-2 rounded-full font-bold transition-all active:scale-95"
            >
              <Plus className="w-5 h-5" />
              Add Organization
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Form */}
        {showForm && (
          <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6 mb-8">
            <h2 className="text-xl font-bold text-slate-900 mb-4">
              {editingOrg ? 'Edit Organization' : 'New Organization'}
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Organization Name</label>
                <input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Tel Aviv Museum of Art"
                  data-testid="org-name-input"
                  className="w-full px-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Contact Email</label>
                <input
                  value={formData.contact_email}
                  onChange={(e) => setFormData(prev => ({ ...prev, contact_email: e.target.value }))}
                  type="email"
                  placeholder="contact@museum.org"
                  data-testid="org-contact-email"
                  className="w-full px-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Report Recipients</label>
                {formData.report_emails.map((email, i) => (
                  <div key={i} className="flex gap-2 mb-2">
                    <input
                      value={email}
                      onChange={(e) => updateEmail(i, e.target.value)}
                      type="email"
                      placeholder="recipient@museum.org"
                      className="flex-1 px-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    />
                    {formData.report_emails.length > 1 && (
                      <button onClick={() => removeEmail(i)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg">
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
                <button onClick={addEmailField} className="text-teal-600 text-sm font-semibold hover:text-teal-700">
                  + Add another email
                </button>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Assign Tours</label>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {tours.map(tour => (
                    <label key={tour.tour_id} className="flex items-center gap-3 p-2 hover:bg-slate-50 rounded-lg cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.tour_ids.includes(tour.tour_id)}
                        onChange={() => toggleTour(tour.tour_id)}
                        className="w-4 h-4 rounded border-slate-400 text-teal-500 focus:ring-teal-500"
                      />
                      <span className="text-sm text-slate-700">{tour.title} <span className="text-slate-400">({tour.city})</span></span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Subscription Plan</label>
                  <select
                    value={formData.plan}
                    onChange={(e) => setFormData(prev => ({ ...prev, plan: e.target.value }))}
                    className="w-full px-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="monthly">Monthly — $99/mo</option>
                    <option value="yearly">Yearly — $899/yr</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Report Frequency</label>
                  <select
                    value={formData.report_frequency}
                    onChange={(e) => setFormData(prev => ({ ...prev, report_frequency: e.target.value }))}
                    className="w-full px-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Notes (internal)</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Internal notes about this organization..."
                  rows={2}
                  className="w-full px-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-teal-500"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={handleSubmit}
                  data-testid="save-org-button"
                  className="bg-teal-500 hover:bg-teal-600 text-white px-6 py-2 rounded-full font-bold transition-all active:scale-95"
                >
                  {editingOrg ? 'Update Organization' : 'Create Organization'}
                </button>
                <button
                  onClick={() => { setShowForm(false); setEditingOrg(null); }}
                  className="text-slate-500 hover:text-slate-700 px-4 py-2"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Organizations List */}
        {organizations.length === 0 && !showForm ? (
          <div className="text-center py-16">
            <Building2 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500 text-lg mb-4">No organizations yet</p>
            <p className="text-slate-400 text-sm">Add your first analytics partner to get started</p>
          </div>
        ) : (
          <div className="space-y-4">
            {organizations.map(org => (
              <div key={org.org_id} className={`bg-white rounded-2xl border-2 p-6 transition-all ${
                org.active ? 'border-green-200' : 'border-slate-200'
              }`} data-testid={`org-card-${org.org_id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-xl font-bold text-slate-900">{org.name}</h3>
                      {org.active ? (
                        <span className="flex items-center gap-1 bg-green-100 text-green-700 text-xs px-2 py-1 rounded-full font-semibold">
                          <Check className="w-3 h-3" /> Active
                        </span>
                      ) : org.subscription_status === 'pending' ? (
                        <span className="flex items-center gap-1 bg-amber-100 text-amber-700 text-xs px-2 py-1 rounded-full font-semibold">
                          <Clock className="w-3 h-3" /> Pending Payment
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 bg-slate-100 text-slate-500 text-xs px-2 py-1 rounded-full font-semibold">
                          <AlertCircle className="w-3 h-3" /> Inactive
                        </span>
                      )}
                    </div>
                    <p className="text-slate-500 text-sm">{org.contact_email}</p>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => handleEdit(org)} className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors">
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDelete(org.org_id)} className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3 text-sm">
                  <div>
                    <span className="text-slate-400 text-xs uppercase">Plan</span>
                    <p className="font-semibold text-slate-700 capitalize">{org.plan} — ${org.plan === 'yearly' ? '899/yr' : '99/mo'}</p>
                  </div>
                  <div>
                    <span className="text-slate-400 text-xs uppercase">Reports</span>
                    <p className="font-semibold text-slate-700 capitalize">{org.report_frequency || 'Monthly'}</p>
                  </div>
                  <div>
                    <span className="text-slate-400 text-xs uppercase">Tours</span>
                    <p className="font-semibold text-slate-700">{org.tour_ids?.length || 0} assigned</p>
                  </div>
                  <div>
                    <span className="text-slate-400 text-xs uppercase">Recipients</span>
                    <p className="font-semibold text-slate-700">{org.report_emails?.length || 0} emails</p>
                  </div>
                </div>

                {org.report_emails?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {org.report_emails.map((email, i) => (
                      <span key={i} className="flex items-center gap-1 bg-slate-100 text-slate-600 text-xs px-2 py-1 rounded-full">
                        <Mail className="w-3 h-3" /> {email}
                      </span>
                    ))}
                  </div>
                )}

                {org.notes && (
                  <p className="text-slate-400 text-xs italic mb-3">{org.notes}</p>
                )}

                {!org.active && (
                  <button
                    onClick={() => handleStartSubscription(org.org_id)}
                    data-testid={`subscribe-${org.org_id}`}
                    className="flex items-center gap-2 bg-indigo-500 hover:bg-indigo-600 text-white px-4 py-2 rounded-lg font-semibold text-sm transition-all active:scale-95"
                  >
                    <CreditCard className="w-4 h-4" />
                    Start Subscription — ${org.plan === 'yearly' ? '899' : '99'}
                  </button>
                )}

                {org.active && org.subscription_expires_at && (
                  <p className="text-green-600 text-xs font-semibold">
                    Subscription active until {new Date(org.subscription_expires_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminOrganizationsPage;
